import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../services/AuthService";

const initialState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  tokenChecked: false, // Track if token verification was attempted
};
// Enhanced Verify Token with proper token check (NO TOAST VERSION)
export const verifyToken = createAsyncThunk(
  "auth/verifyToken", 
  async (_, { rejectWithValue }) => {
    try {
      // Check if token exists and is valid before making request
      const token = localStorage.getItem("token");
      if (!token || token === "undefined" || token === "null") {
        // ✅ SILENT REJECTION - No toast needed for missing token
        return rejectWithValue({
          statusCode: 'NO_TOKEN',
          message: "No valid token found",
          originalResponse: null,
          silent: true // ✅ Flag to prevent toast display
        });
      }
      
      const response = await authService.verifyToken();
      const { statusCode, message, data } = response;
      
      // Success case (statusCode 2000)
      if (statusCode === 2000) {
        return {
          statusCode,
          message,
          user: data,
          token: token
        };
      } else {
        // Error cases - remove invalid token
        localStorage.removeItem("token");
        return rejectWithValue({
          statusCode,
          message,
          originalResponse: response,
          silent: true // ✅ Flag to prevent toast display
        });
      }
    } catch (error) {
      localStorage.removeItem("token");
      return rejectWithValue({
        statusCode: error.statusCode || 'NETWORK_ERROR',
        message: error.message || "Token verification failed",
        originalResponse: error.response || null,
        silent: true // ✅ Flag to prevent toast display
      });
    }
  }
);

// Enhanced Login - with global axios interceptor handling
export const login = createAsyncThunk(
  "auth/login", 
  async ({ email, password, googleData }, { rejectWithValue }) => {
    try {
      const requestBody = googleData
        ? {
            name: googleData.name,
            email: googleData.email,
            picture: googleData.picture,
            googleId: googleData.googleId,
            password: googleData.googleId,
            emailVerified: googleData.emailVerified,
            provider: "GOOGLE",
          }
        : { email, password, provider: "LOCAL" };

      // ✅ Axios interceptor handles custom status codes globally
      const response = await authService.login(requestBody);
      
      // ✅ If we reach here, statusCode was 2000 (success)
      const { statusCode, message, data } = response;
      
      if (data?.token) {
        localStorage.setItem("token", data.token);
      }
      
      return {
        statusCode,
        message,
        user: data,
        token: data?.token
      };
      
    } catch (error) {
      // ✅ Interceptor provides structured error objects
      return rejectWithValue({
        statusCode: error.statusCode || 'NETWORK_ERROR',
        message: error.message || "Login failed",
        originalResponse: error.response || null
      });
    }
  }
);

// Enhanced Register - with global axios interceptor handling
export const register = createAsyncThunk(
  "auth/register", 
  async (userData, { rejectWithValue }) => {
    try {
      const requestBody = userData.registrationMethod === "google"
        ? {
            name: userData.name,
            email: userData.email,
            picture: userData.picture,
            googleId: userData.googleId,
            password: userData.googleId,
            emailVerified: userData.emailVerified,
            provider: "GOOGLE",
          }
        : { 
            name: userData.name, 
            email: userData.email, 
            password: userData.password, 
            provider: "LOCAL" 
          };

      // ✅ Axios interceptor handles custom status codes globally
      const response = await authService.register(requestBody);
      
      // ✅ If we reach here, statusCode was 2000 (success)
      const { statusCode, message, data } = response;
      
      if (data?.token) {
        localStorage.setItem("token", data.token);
      }
      
      return {
        statusCode,
        message,
        user: data,
        token: data?.token
      };
      
    } catch (error) {
      // ✅ Interceptor provides structured error objects
      return rejectWithValue({
        statusCode: error.statusCode || 'NETWORK_ERROR',
        message: error.message || "Registration failed",
        originalResponse: error.response || null
      });
    }
  }
);

// Forgot Password AsyncThunk
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      
      // ✅ If we reach here, statusCode was 2000 (success)
      const { statusCode, message, data } = response;
      
      return {
        statusCode,
        message,
        data
      };
    } catch (error) {
      return rejectWithValue({
        statusCode: error.statusCode || 'NETWORK_ERROR',
        message: error.message || "Failed to send reset email",
        originalResponse: error.response || null
      });
    }
  }
);

// Reset Password AsyncThunk
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(token, password);
      
      // ✅ If we reach here, statusCode was 2000 (success)
      const { statusCode, message, data } = response;
      
      return {
        statusCode,
        message,
        data
      };
    } catch (error) {
      return rejectWithValue({
        statusCode: error.statusCode || 'NETWORK_ERROR',
        message: error.message || "Failed to reset password",
        originalResponse: error.response || null
      });
    }
  }
);

// Update Profile AsyncThunk
export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(userData);
      
      // ✅ If we reach here, statusCode was 2000 (success)
      const { statusCode, message, data } = response;
      
      return {
        statusCode,
        message,
        user: data
      };
    } catch (error) {
      return rejectWithValue({
        statusCode: error.statusCode || 'NETWORK_ERROR',
        message: error.message || "Failed to update profile",
        originalResponse: error.response || null
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.tokenChecked = true;
      state.error = null;
      localStorage.removeItem("token");
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAuthState: (state) => {
      state.error = null;
      state.loading = false;
    },
    setTokenChecked: (state, action) => {
      state.tokenChecked = action.payload;
    },
    updateUserData: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Verify Token cases
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokenChecked = true;
        state.error = null;
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.tokenChecked = true;
        state.user = null;
      })
      
      // Login cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokenChecked = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      
      // Register cases
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.tokenChecked = true;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      
      // Forgot Password cases
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Reset Password cases
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Profile cases
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError, resetAuthState, setTokenChecked, updateUserData } = authSlice.actions;
export default authSlice.reducer;
